// src/pages/Backoffice.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Users, Plus, Shield, X, Key, Search, ChevronRight,
    Edit3, Lock, Building2, MapPin, UserCheck, UserX,
    AlertCircle, Check, Loader2, RefreshCw, Filter,
    ChevronDown, Eye, EyeOff, Trash2, Save
} from 'lucide-react';
import { apiFetch } from '../utils/apiFetch';
import { toast } from 'sonner';
import { Navigate } from 'react-router-dom'; // <-- Adicionar isto

// ─── Permissões disponíveis no sistema (fonte única da verdade) ──────────────
// Para adicionar uma nova permissão ao sistema inteiro, basta adicionar aqui.
// O Backoffice é o único lugar onde isso é definido.
const PERMISSOES_SISTEMA = [
    {
        id: 'beta_lancamento',
        label: 'Lançamento de Visitas',
        descricao: 'Acesso à página de lançamento de visitas (Beta)',
        cor: '#3b82f6',
    },
    // Exemplo futuro:
    // { id: 'exportar_relatorios', label: 'Exportar Relatórios', descricao: 'Exportar dados em Excel/PDF', cor: '#10b981' },
    // { id: 'visualizar_mapa', label: 'Mapa de Cobertura', descricao: 'Acesso ao mapa de microáreas', cor: '#8b5cf6' },
];

const CARGOS = ['ACS', 'SUPERVISOR', 'ADMIN'];

const CARGO_CONFIG = {
    ADMIN:      { bg: '#fef2f2', color: '#dc2626', border: '#fca5a5', label: 'Admin' },
    SUPERVISOR: { bg: '#fffbeb', color: '#d97706', border: '#fcd34d', label: 'Supervisor' },
    ACS:        { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe', label: 'ACS' },
};

// ─── Estilos base (inline para portabilidade total) ──────────────────────────
const S = {
    page: {
        minHeight: '100vh',
        background: '#f8fafc',
        fontFamily: "'IBM Plex Sans', 'Segoe UI', sans-serif",
    },
    inner: {
        maxWidth: '1300px',
        margin: '0 auto',
        padding: '32px 24px',
    },
    // Header
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '32px',
        gap: '16px',
        flexWrap: 'wrap',
    },
    title: {
        fontSize: '1.25rem',
        fontWeight: '700',
        color: '#0f172a',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        margin: 0,
        letterSpacing: '-0.02em',
    },
    subtitle: { color: '#64748b', fontSize: '0.85rem', margin: '4px 0 0', fontWeight: '400' },
    // Stats row
    statsRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '12px',
        marginBottom: '24px',
    },
    statCard: {
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        padding: '16px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    statLabel: { fontSize: '0.75rem', color: '#94a3b8', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' },
    statValue: { fontSize: '1.75rem', fontWeight: '800', color: '#0f172a', lineHeight: 1 },
    // Toolbar
    toolbar: {
        display: 'flex',
        gap: '10px',
        marginBottom: '16px',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    searchWrap: {
        position: 'relative',
        flex: '1',
        minWidth: '200px',
    },
    searchInput: {
        width: '100%',
        padding: '9px 12px 9px 36px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '0.875rem',
        background: '#fff',
        color: '#0f172a',
        outline: 'none',
        boxSizing: 'border-box',
        transition: 'border-color 0.15s',
    },
    searchIcon: {
        position: 'absolute',
        left: '11px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#94a3b8',
        pointerEvents: 'none',
    },
    filterSelect: {
        padding: '9px 32px 9px 12px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '0.875rem',
        background: '#fff',
        color: '#0f172a',
        outline: 'none',
        cursor: 'pointer',
        appearance: 'none',
        WebkitAppearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 8px center',
    },
    // Table
    tableWrap: {
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        overflow: 'hidden',
    },
    table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
    thead: { background: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
    th: { padding: '11px 16px', color: '#64748b', fontWeight: '600', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' },
    tr: (hovered) => ({
        borderBottom: '1px solid #f1f5f9',
        background: hovered ? '#f8fafc' : 'transparent',
        cursor: 'pointer',
        transition: 'background 0.1s',
    }),
    td: { padding: '13px 16px', color: '#334155', fontSize: '0.875rem' },
    tdName: { padding: '13px 16px', fontWeight: '600', color: '#0f172a', fontSize: '0.875rem' },
    // Badges
    badge: (cargo) => ({
        display: 'inline-flex',
        alignItems: 'center',
        padding: '3px 9px',
        borderRadius: '20px',
        fontSize: '0.73rem',
        fontWeight: '700',
        letterSpacing: '0.03em',
        background: CARGO_CONFIG[cargo]?.bg || '#f1f5f9',
        color: CARGO_CONFIG[cargo]?.color || '#64748b',
        border: `1px solid ${CARGO_CONFIG[cargo]?.border || '#e2e8f0'}`,
    }),
    permBadge: (cor) => ({
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 8px',
        borderRadius: '4px',
        fontSize: '0.72rem',
        fontWeight: '600',
        background: cor + '18',
        color: cor,
        border: `1px solid ${cor}40`,
        marginRight: '4px',
        marginBottom: '2px',
    }),
    emptyCell: { padding: '48px', textAlign: 'center', color: '#94a3b8', fontSize: '0.875rem' },
    // Buttons
    btnPrimary: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        padding: '9px 18px',
        background: '#0f172a',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        fontSize: '0.875rem',
        fontWeight: '600',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'background 0.15s',
    },
    btnSecondary: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        padding: '9px 16px',
        background: '#fff',
        color: '#374151',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '0.875rem',
        fontWeight: '500',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'background 0.15s',
    },
    btnDanger: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        padding: '9px 16px',
        background: '#fff',
        color: '#dc2626',
        border: '1px solid #fca5a5',
        borderRadius: '8px',
        fontSize: '0.875rem',
        fontWeight: '600',
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        transition: 'all 0.15s',
    },
    // Overlay / Panel
    overlay: {
        position: 'fixed', inset: 0,
        background: 'rgba(15,23,42,0.35)',
        backdropFilter: 'blur(2px)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'flex-end',
    },
    panel: {
        width: '460px',
        maxWidth: '100vw',
        background: '#fff',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '-8px 0 32px rgba(15,23,42,0.12)',
        animation: 'slideInRight 0.2s ease-out',
        overflow: 'hidden',
    },
    panelHeader: {
        padding: '20px 24px',
        borderBottom: '1px solid #e2e8f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
    },
    panelTitle: { fontSize: '1rem', fontWeight: '700', color: '#0f172a', margin: 0 },
    panelBody: {
        padding: '24px',
        overflowY: 'auto',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    panelFooter: {
        padding: '16px 24px',
        borderTop: '1px solid #e2e8f0',
        display: 'flex',
        gap: '10px',
        justifyContent: 'flex-end',
        flexShrink: 0,
        background: '#f8fafc',
    },
    // Form
    formGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
    label: { fontSize: '0.8rem', fontWeight: '600', color: '#374151', letterSpacing: '0.01em' },
    input: {
        padding: '9px 12px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '0.875rem',
        color: '#0f172a',
        outline: 'none',
        background: '#fff',
        transition: 'border-color 0.15s',
        width: '100%',
        boxSizing: 'border-box',
    },
    inputDisabled: {
        padding: '9px 12px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '0.875rem',
        color: '#94a3b8',
        background: '#f8fafc',
        width: '100%',
        boxSizing: 'border-box',
    },
    select: {
        padding: '9px 32px 9px 12px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        fontSize: '0.875rem',
        color: '#0f172a',
        background: '#fff',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
        appearance: 'none',
        WebkitAppearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 8px center',
        cursor: 'pointer',
    },
    grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
    sectionTitle: {
        fontSize: '0.75rem',
        fontWeight: '700',
        color: '#94a3b8',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        margin: '0 0 12px',
    },
    // Permission toggle
    permRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 14px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        marginBottom: '8px',
        background: '#fafafa',
        transition: 'all 0.15s',
    },
    toggle: (active) => ({
        width: '38px',
        height: '22px',
        borderRadius: '11px',
        background: active ? '#0f172a' : '#e2e8f0',
        position: 'relative',
        cursor: 'pointer',
        border: 'none',
        transition: 'background 0.2s',
        flexShrink: 0,
    }),
    toggleDot: (active) => ({
        position: 'absolute',
        top: '3px',
        left: active ? '19px' : '3px',
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        background: '#fff',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    }),
    // Icon button
    iconBtn: {
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '6px',
        borderRadius: '6px',
        color: '#64748b',
        display: 'flex',
        alignItems: 'center',
        transition: 'background 0.15s, color 0.15s',
    },
    // Alert
    alert: (type) => ({
        padding: '10px 14px',
        borderRadius: '8px',
        fontSize: '0.8rem',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        background: type === 'warning' ? '#fffbeb' : '#eff6ff',
        border: `1px solid ${type === 'warning' ? '#fcd34d' : '#bfdbfe'}`,
        color: type === 'warning' ? '#92400e' : '#1e40af',
    }),
    // Divider
    divider: { border: 'none', borderTop: '1px solid #e2e8f0', margin: '4px 0' },
};

// ─── Componente principal ────────────────────────────────────────────────────
export default function Backoffice() {
    const currentUserStr = localStorage.getItem('user');
    const currentUser = currentUserStr ? JSON.parse(currentUserStr) : null;
    const isAdmin = currentUser?.cargo === 'ADMIN';
    const podeVerAdmin = currentUser?.cargo === 'ADMIN' || currentUser?.cargo === 'SUPERVISOR';

    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState('');
    const [filterCargo, setFilterCargo] = useState('');
    const [hoveredRow, setHoveredRow] = useState(null);

    // Painel de edição (deslizante)
    const [editUser, setEditUser] = useState(null);
    const [editForm, setEditForm] = useState({});

    // Modal de criação
    const [showCreate, setShowCreate] = useState(false);
    const [createForm, setCreateForm] = useState({
        nome: '', login: '', senha: '', cargo: 'ACS',
        microarea: '', unidade: currentUser.unidade || '',
        permissoes: [],
    });
    const [showSenha, setShowSenha] = useState(false);

    // ── Carregamento ──
    const loadUsuarios = useCallback(async () => {
        if (!podeVerAdmin) return; // 🛡️ TRAVA: Impede o fetch se não tiver permissão!

        setLoading(true);
        try {
            const res = await apiFetch('/api/admin/usuarios');
            const result = await res.json();
            setUsuarios(result.data || []);
        } catch {
            toast.error('Erro ao carregar usuários.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadUsuarios(); }, [loadUsuarios]);

    // ── Filtragem ──
    const filtered = usuarios.filter(u => {
        const termo = search.toLowerCase();
        const matchSearch = !search ||
            u.nome?.toLowerCase().includes(termo) ||
            u.login?.toLowerCase().includes(termo) ||
            u.unidade?.toLowerCase().includes(termo) ||
            u.microarea?.toLowerCase().includes(termo);
        const matchCargo = !filterCargo || u.cargo === filterCargo;
        // Supervisor só vê usuários da própria unidade
        const matchUnidade = isAdmin || !currentUser.unidade || u.unidade === currentUser.unidade || u.id === currentUser.id;
        return matchSearch && matchCargo && matchUnidade;
    });

    // ── Stats ──
    const stats = {
        total: filtered.length,
        admins: filtered.filter(u => u.cargo === 'ADMIN').length,
        supervisores: filtered.filter(u => u.cargo === 'SUPERVISOR').length,
        acs: filtered.filter(u => u.cargo === 'ACS').length,
    };

    // ── Abrir painel de edição ──
    const openEdit = (u) => {
        setEditUser(u);
        setEditForm({
            nome: u.nome || '',
            cargo: u.cargo || 'ACS',
            microarea: u.microarea || '',
            unidade: u.unidade || '',
            permissoes: Array.isArray(u.permissoes) ? [...u.permissoes] : [],
            nova_senha: '',
        });
    };

    // ── Toggle permissão no painel de edição ──
    const togglePermEdit = (permId) => {
        setEditForm(prev => ({
            ...prev,
            permissoes: prev.permissoes.includes(permId)
                ? prev.permissoes.filter(p => p !== permId)
                : [...prev.permissoes, permId],
        }));
    };

    // ── Salvar edição ──
    const handleSaveEdit = async () => {
        setSaving(true);
        try {
            const payload = {
                nome: editForm.nome,
                cargo: editForm.cargo,
                microarea: editForm.microarea,
                unidade: editForm.unidade,
                permissoes: editForm.permissoes,
            };
            if (editForm.nova_senha.trim()) {
                payload.senha = editForm.nova_senha.trim();
            }
            await apiFetch(`/api/admin/usuarios/${editUser.id}`, {
                method: 'PATCH',
                body: JSON.stringify(payload),
            });
            toast.success('Usuário atualizado!');
            setEditUser(null);
            loadUsuarios();
        } catch {
            toast.error('Erro ao salvar alterações.');
        } finally {
            setSaving(false);
        }
    };

    // ── Criar usuário ──
    const handleCreate = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await apiFetch('/api/admin/usuarios', {
                method: 'POST',
                body: JSON.stringify(createForm),
            });
            toast.success('Usuário criado com sucesso!');
            setShowCreate(false);
            setCreateForm({ nome: '', login: '', senha: '', cargo: 'ACS', microarea: '', unidade: currentUser.unidade || '', permissoes: [] });
            loadUsuarios();
        } catch {
            toast.error('Erro ao criar usuário. Verifique se o login já existe.');
        } finally {
            setSaving(false);
        }
    };

    // ── Toggle permissão no formulário de criação ──
    const togglePermCreate = (permId) => {
        setCreateForm(prev => ({
            ...prev,
            permissoes: prev.permissoes.includes(permId)
                ? prev.permissoes.filter(p => p !== permId)
                : [...prev.permissoes, permId],
        }));
    };

    // ── Verificação de acesso: quais cargos o usuário atual pode criar/editar ──
    const cargosPermitidos = isAdmin ? CARGOS : ['ACS'];

    if (!podeVerAdmin) return <Navigate to="/" replace />;
    
    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div style={S.page}>
            <style>{`
                @keyframes slideInRight {
                    from { transform: translateX(100%); opacity: 0; }
                    to   { transform: translateX(0);    opacity: 1; }
                }
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to   { opacity: 1; }
                }
                input:focus, select:focus { border-color: #0f172a !important; }
                .bo-tr:hover td { background: #f8fafc; }
                .icon-btn-hover:hover { background: #f1f5f9; color: #0f172a; }
                .btn-primary-hover:hover { background: #1e293b; }
                .btn-secondary-hover:hover { background: #f8fafc; }
                .btn-danger-hover:hover { background: #fef2f2; }
            `}</style>

            <div style={S.inner}>
                {/* ── Header ── */}
                <div style={S.header}>
                    <div>
                        <h1 style={S.title}>
                            <Shield size={22} color="#3b82f6" />
                            Painel de Controle
                        </h1>
                        <p style={S.subtitle}>
                            {isAdmin ? 'Gestão global de usuários e acessos' : `Gestão da unidade ${currentUser.unidade || '—'}`}
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <button
                            className="btn-secondary-hover"
                            style={S.btnSecondary}
                            onClick={loadUsuarios}
                            disabled={loading}
                        >
                            <RefreshCw size={15} style={{ animation: loading ? 'spin 1s linear infinite' : 'none' }} />
                            Atualizar
                        </button>
                        <button
                            className="btn-primary-hover"
                            style={S.btnPrimary}
                            onClick={() => setShowCreate(true)}
                        >
                            <Plus size={15} />
                            Novo Usuário
                        </button>
                    </div>
                </div>

                {/* ── Stats ── */}
                <div style={S.statsRow}>
                    {[
                        { label: 'Total', value: stats.total, color: '#0f172a' },
                        { label: 'Admins', value: stats.admins, color: CARGO_CONFIG.ADMIN.color },
                        { label: 'Supervisores', value: stats.supervisores, color: CARGO_CONFIG.SUPERVISOR.color },
                        { label: 'Agentes', value: stats.acs, color: CARGO_CONFIG.ACS.color },
                    ].map(s => (
                        <div key={s.label} style={S.statCard}>
                            <span style={S.statLabel}>{s.label}</span>
                            <span style={{ ...S.statValue, color: s.color }}>{s.value}</span>
                        </div>
                    ))}
                </div>

                {/* ── Toolbar ── */}
                <div style={S.toolbar}>
                    <div style={S.searchWrap}>
                        <Search size={15} style={S.searchIcon} />
                        <input
                            style={S.searchInput}
                            placeholder="Buscar por nome, login, unidade..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                    <select
                        style={S.filterSelect}
                        value={filterCargo}
                        onChange={e => setFilterCargo(e.target.value)}
                    >
                        <option value="">Todos os cargos</option>
                        {CARGOS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                </div>

                {/* ── Tabela ── */}
                <div style={S.tableWrap}>
                    {loading ? (
                        <div style={{ ...S.emptyCell, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
                            <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                            Carregando usuários...
                        </div>
                    ) : (
                        <table style={S.table}>
                            <thead style={S.thead}>
                                <tr>
                                    <th style={S.th}>Usuário</th>
                                    <th style={S.th}>Login</th>
                                    <th style={S.th}>Cargo</th>
                                    {isAdmin && <th style={S.th}>Unidade</th>}
                                    <th style={S.th}>Microárea</th>
                                    <th style={S.th}>Permissões</th>
                                    <th style={{ ...S.th, width: '48px' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={isAdmin ? 7 : 6} style={S.emptyCell}>
                                            Nenhum usuário encontrado.
                                        </td>
                                    </tr>
                                ) : filtered.map(u => (
                                    <tr
                                        key={u.id}
                                        className="bo-tr"
                                        style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}
                                        onClick={() => openEdit(u)}
                                        onMouseEnter={() => setHoveredRow(u.id)}
                                        onMouseLeave={() => setHoveredRow(null)}
                                    >
                                        <td style={S.tdName}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{
                                                    width: '32px', height: '32px', borderRadius: '50%',
                                                    background: CARGO_CONFIG[u.cargo]?.bg || '#f1f5f9',
                                                    border: `1px solid ${CARGO_CONFIG[u.cargo]?.border || '#e2e8f0'}`,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    fontSize: '0.75rem', fontWeight: '800',
                                                    color: CARGO_CONFIG[u.cargo]?.color || '#64748b',
                                                    flexShrink: 0,
                                                }}>
                                                    {u.nome?.charAt(0) || '?'}
                                                </div>
                                                <span>{u.nome}</span>
                                            </div>
                                        </td>
                                        <td style={{ ...S.td, fontFamily: 'monospace', fontSize: '0.82rem', color: '#475569' }}>
                                            {u.login}
                                        </td>
                                        <td style={S.td}>
                                            <span style={S.badge(u.cargo)}>{u.cargo}</span>
                                        </td>
                                        {isAdmin && (
                                            <td style={{ ...S.td, color: '#64748b' }}>
                                                {u.unidade || <span style={{ color: '#cbd5e1' }}>—</span>}
                                            </td>
                                        )}
                                        <td style={{ ...S.td, color: '#64748b', maxWidth: '200px' }}>
                                            {u.microarea
                                                ? <span style={{ fontSize: '0.8rem' }}>{u.microarea}</span>
                                                : <span style={{ color: '#cbd5e1' }}>—</span>
                                            }
                                        </td>
                                        <td style={S.td}>
                                            {Array.isArray(u.permissoes) && u.permissoes.length > 0
                                                ? u.permissoes.map(pId => {
                                                    const p = PERMISSOES_SISTEMA.find(x => x.id === pId);
                                                    return p
                                                        ? <span key={pId} style={S.permBadge(p.cor)}><Key size={10}/>{p.label}</span>
                                                        : <span key={pId} style={S.permBadge('#94a3b8')}>{pId}</span>;
                                                })
                                                : <span style={{ color: '#cbd5e1', fontSize: '0.8rem' }}>Padrão</span>
                                            }
                                        </td>
                                        <td style={{ ...S.td, textAlign: 'right' }}>
                                            <ChevronRight size={16} style={{ color: hoveredRow === u.id ? '#0f172a' : '#cbd5e1', transition: 'color 0.15s' }} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* ── Rodapé ── */}
                <p style={{ marginTop: '12px', color: '#94a3b8', fontSize: '0.78rem', textAlign: 'right' }}>
                    {filtered.length} de {usuarios.length} usuário{usuarios.length !== 1 ? 's' : ''}
                </p>
            </div>

            {/* ════ PAINEL DE EDIÇÃO (slide-over) ════ */}
            {editUser && (
                <div style={{ ...S.overlay, animation: 'fadeIn 0.15s' }} onClick={(e) => { if (e.target === e.currentTarget) setEditUser(null); }}>
                    <div style={S.panel}>
                        {/* Header */}
                        <div style={S.panelHeader}>
                            <div>
                                <h3 style={S.panelTitle}>Editar Usuário</h3>
                                <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>{editUser.login}</p>
                            </div>
                            <button className="icon-btn-hover" style={S.iconBtn} onClick={() => setEditUser(null)}>
                                <X size={18} />
                            </button>
                        </div>

                        {/* Body */}
                        <div style={S.panelBody}>
                            {/* Aviso: usuário logado editando a si mesmo */}
                            {editUser.id === currentUser.id && (
                                <div style={S.alert('warning')}>
                                    <AlertCircle size={15} style={{ flexShrink: 0, marginTop: '1px' }} />
                                    <span>Você está editando sua própria conta. Alterações no cargo têm efeito imediato no próximo login.</span>
                                </div>
                            )}

                            {/* Info básica */}
                            <div>
                                <p style={S.sectionTitle}>Informações</p>
                                <div style={S.formGroup}>
                                    <label style={S.label}>Nome Completo</label>
                                    <input
                                        style={S.input}
                                        value={editForm.nome}
                                        onChange={e => setEditForm({ ...editForm, nome: e.target.value })}
                                    />
                                </div>
                            </div>

                            <hr style={S.divider} />

                            {/* Cargo e Unidade */}
                            <div>
                                <p style={S.sectionTitle}>Acesso</p>
                                <div style={S.grid2}>
                                    <div style={S.formGroup}>
                                        <label style={S.label}>Cargo</label>
                                        {isAdmin ? (
                                            <select
                                                style={S.select}
                                                value={editForm.cargo}
                                                onChange={e => setEditForm({ ...editForm, cargo: e.target.value })}
                                            >
                                                {CARGOS.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        ) : (
                                            <div style={S.inputDisabled}>{editForm.cargo}</div>
                                        )}
                                    </div>
                                    <div style={S.formGroup}>
                                        <label style={S.label}>Unidade (UBS)</label>
                                        {isAdmin ? (
                                            <input
                                                style={S.input}
                                                value={editForm.unidade}
                                                onChange={e => setEditForm({ ...editForm, unidade: e.target.value })}
                                                placeholder="Ex: UBS Central"
                                            />
                                        ) : (
                                            <div style={S.inputDisabled}>{editForm.unidade || '—'}</div>
                                        )}
                                    </div>
                                </div>
                                {(editForm.cargo === 'ACS' || editForm.cargo === 'SUPERVISOR') && (
                                    <div style={{ ...S.formGroup, marginTop: '14px' }}>
                                        <label style={S.label}>Microárea(s)</label>
                                        <input
                                            style={S.input}
                                            value={editForm.microarea}
                                            onChange={e => setEditForm({ ...editForm, microarea: e.target.value })}
                                            placeholder="Ex: MICROAREA 01 EQ 0060, MICROAREA 02..."
                                        />
                                        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Separe múltiplas microáreas por vírgula</span>
                                    </div>
                                )}
                            </div>

                            <hr style={S.divider} />

                            {/* Permissões */}
                            <div>
                                <p style={S.sectionTitle}>
                                    <Key size={12} style={{ display: 'inline', marginRight: '4px' }} />
                                    Permissões Especiais
                                </p>
                                {PERMISSOES_SISTEMA.map(perm => {
                                    const ativo = editForm.permissoes.includes(perm.id);
                                    return (
                                        <div key={perm.id} style={{ ...S.permRow, borderColor: ativo ? perm.cor + '50' : '#e2e8f0', background: ativo ? perm.cor + '08' : '#fafafa' }}>
                                            <div>
                                                <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#0f172a' }}>{perm.label}</div>
                                                <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '2px' }}>{perm.descricao}</div>
                                            </div>
                                            <button style={S.toggle(ativo)} onClick={() => togglePermEdit(perm.id)}>
                                                <div style={S.toggleDot(ativo)} />
                                            </button>
                                        </div>
                                    );
                                })}
                                {PERMISSOES_SISTEMA.length === 0 && (
                                    <p style={{ color: '#94a3b8', fontSize: '0.8rem' }}>Nenhuma permissão especial disponível.</p>
                                )}
                            </div>

                            <hr style={S.divider} />

                            {/* Redefinir senha */}
                            <div>
                                <p style={S.sectionTitle}><Lock size={12} style={{ display: 'inline', marginRight: '4px' }} />Segurança</p>
                                <div style={S.formGroup}>
                                    <label style={S.label}>Nova Senha (deixe em branco para não alterar)</label>
                                    <div style={{ position: 'relative' }}>
                                        <input
                                            type={showSenha ? 'text' : 'password'}
                                            style={{ ...S.input, paddingRight: '40px' }}
                                            value={editForm.nova_senha}
                                            onChange={e => setEditForm({ ...editForm, nova_senha: e.target.value })}
                                            placeholder="Nova senha..."
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowSenha(!showSenha)}
                                            style={{ ...S.iconBtn, position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', padding: '4px' }}
                                        >
                                            {showSenha ? <EyeOff size={15} /> : <Eye size={15} />}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={S.panelFooter}>
                            <button className="btn-secondary-hover" style={S.btnSecondary} onClick={() => setEditUser(null)}>
                                Cancelar
                            </button>
                            <button
                                className="btn-primary-hover"
                                style={S.btnPrimary}
                                onClick={handleSaveEdit}
                                disabled={saving}
                            >
                                {saving ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={15} />}
                                Salvar Alterações
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ════ MODAL DE CRIAÇÃO ════ */}
            {showCreate && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', backdropFilter: 'blur(3px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', animation: 'fadeIn 0.15s' }}>
                    <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '520px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(15,23,42,0.2)', overflow: 'hidden' }}>
                        {/* Header modal */}
                        <div style={S.panelHeader}>
                            <div>
                                <h3 style={S.panelTitle}>Cadastrar Usuário</h3>
                                <p style={{ margin: '2px 0 0', fontSize: '0.8rem', color: '#94a3b8' }}>Novo acesso ao sistema</p>
                            </div>
                            <button className="icon-btn-hover" style={S.iconBtn} onClick={() => setShowCreate(false)}>
                                <X size={18} />
                            </button>
                        </div>

                        {/* Body modal */}
                        <form onSubmit={handleCreate} style={{ ...S.panelBody, gap: '16px' }}>
                            <div style={S.formGroup}>
                                <label style={S.label}>Nome Completo *</label>
                                <input required style={S.input} value={createForm.nome} onChange={e => setCreateForm({ ...createForm, nome: e.target.value })} placeholder="Nome do usuário" />
                            </div>

                            <div style={S.grid2}>
                                <div style={S.formGroup}>
                                    <label style={S.label}>Login *</label>
                                    <input required style={S.input} value={createForm.login} onChange={e => setCreateForm({ ...createForm, login: e.target.value.toLowerCase().trim() })} placeholder="login.usuario" />
                                </div>
                                <div style={S.formGroup}>
                                    <label style={S.label}>Senha Provisória *</label>
                                    <input required style={S.input} type="text" value={createForm.senha} onChange={e => setCreateForm({ ...createForm, senha: e.target.value })} placeholder="Senha inicial" />
                                </div>
                            </div>

                            <div style={S.grid2}>
                                <div style={S.formGroup}>
                                    <label style={S.label}>Cargo *</label>
                                    <select style={S.select} value={createForm.cargo} onChange={e => setCreateForm({ ...createForm, cargo: e.target.value })}>
                                        {cargosPermitidos.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div style={S.formGroup}>
                                    <label style={S.label}>Unidade (UBS)</label>
                                    {isAdmin
                                        ? <input style={S.input} value={createForm.unidade} onChange={e => setCreateForm({ ...createForm, unidade: e.target.value })} placeholder="Ex: UBS Central" />
                                        : <div style={S.inputDisabled}>{createForm.unidade || '—'}</div>
                                    }
                                </div>
                            </div>

                            {(createForm.cargo === 'ACS') && (
                                <div style={S.formGroup}>
                                    <label style={S.label}>Microárea(s)</label>
                                    <input style={S.input} value={createForm.microarea} onChange={e => setCreateForm({ ...createForm, microarea: e.target.value })} placeholder="Ex: MICROAREA 01, MICROAREA 02" />
                                    <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Separe por vírgula</span>
                                </div>
                            )}

                            <hr style={S.divider} />

                            {/* Permissões na criação */}
                            <div>
                                <p style={S.sectionTitle}><Key size={12} style={{ display: 'inline', marginRight: '4px' }} />Permissões Especiais</p>
                                {PERMISSOES_SISTEMA.map(perm => {
                                    const ativo = createForm.permissoes.includes(perm.id);
                                    return (
                                        <div key={perm.id} style={{ ...S.permRow, borderColor: ativo ? perm.cor + '50' : '#e2e8f0', background: ativo ? perm.cor + '08' : '#fafafa' }}>
                                            <div>
                                                <div style={{ fontSize: '0.84rem', fontWeight: '600', color: '#0f172a' }}>{perm.label}</div>
                                                <div style={{ fontSize: '0.73rem', color: '#94a3b8' }}>{perm.descricao}</div>
                                            </div>
                                            <button type="button" style={S.toggle(ativo)} onClick={() => togglePermCreate(perm.id)}>
                                                <div style={S.toggleDot(ativo)} />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Footer modal */}
                            <div style={{ ...S.panelFooter, margin: '4px -24px -24px', padding: '14px 24px' }}>
                                <button type="button" className="btn-secondary-hover" style={S.btnSecondary} onClick={() => setShowCreate(false)}>Cancelar</button>
                                <button type="submit" className="btn-primary-hover" style={S.btnPrimary} disabled={saving}>
                                    {saving ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <UserCheck size={15} />}
                                    Criar Usuário
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}